import { createContext, FC, PropsWithChildren, use, useState } from 'react';

import { GProduct } from '@/types';
import { useDebounced } from '@/hooks';

import { useSearchProductsByTerm } from '../hooks';

interface SearchContextState {
  products: GProduct[];
  isLoadingProducts: boolean;
  searchTerm: string;
  isErrorProductsByTerm?: boolean;
  errorProductsByTerm: Error | null;
  updateSearchTerm(_term: string): void;
}

const SearchContext = createContext<SearchContextState | undefined>(undefined);

SearchContext.displayName = 'Search Context';

export const SearchProvider: FC<PropsWithChildren> = ({ children }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounced(searchTerm, 300);

  const {
    products,
    isLoading: isLoadingProducts,
    isError: isErrorProductsByTerm,
    error: errorProductsByTerm,
  } = useSearchProductsByTerm(debouncedSearchTerm);

  return (
    <SearchContext
      value={{
        isLoadingProducts,
        products,
        searchTerm,
        isErrorProductsByTerm,
        errorProductsByTerm,
        updateSearchTerm: setSearchTerm,
      }}
    >
      {children}
    </SearchContext>
  );
};

const useSearchContext = (label: string) => {
  const searchContext = use(SearchContext);

  if (!searchContext) {
    throw new Error(`${label} must be used within a SearchProvider`);
  }

  return searchContext;
};

export const useSearchTerm = () => {
  const { searchTerm, updateSearchTerm } = useSearchContext('useSearchTerm');

  return { searchTerm, updateSearchTerm };
};

export const useSearchProducts = () => {
  const {
    products,
    isLoadingProducts,
    isErrorProductsByTerm,
    errorProductsByTerm,
  } = useSearchContext('useSearchProducts');

  return {
    isLoadingProducts,
    products,
    isErrorProductsByTerm,
    errorProductsByTerm,
  };
};
